import { Box, Divider, Paper, styled, Typography } from '@mui/material';

import theme from 'features/theme/theme';

export const DebugContainer = styled(Paper)(() => ({
  position: 'fixed',
  bottom: theme.spacing(2),
  right: theme.spacing(2),
  padding: theme.spacing(2.5),
  maxWidth: '720px',
  minWidth: '540px',
  minHeight: '320px',
  maxHeight: '80vh',
  overflowY: 'auto',
  zIndex: 9999,
  opacity: 0.97,
  backgroundColor: '#0f1419',
  color: '#e2e8f0',
  borderRadius: 12,
  border: '1px solid rgba(99, 102, 241, 0.15)',
  boxShadow:
    '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(99, 102, 241, 0.08)',
  backdropFilter: 'blur(12px)',
  '&:hover': {
    opacity: 1,
  },
  '&::-webkit-scrollbar': {
    width: 6,
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(99, 102, 241, 0.3)',
    borderRadius: 3,
  },
}));

export const Section = styled(Box)(() => ({
  marginBottom: theme.spacing(2.5),
  '&:last-child': {
    marginBottom: 0,
  },
}));

export const SectionTitle = styled(Typography)(() => ({
  color: '#818cf8',
  fontWeight: 600,
  marginBottom: theme.spacing(1.5),
  fontSize: '0.85em',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  paddingBottom: theme.spacing(0.75),
  borderBottom: '1px solid rgba(129, 140, 248, 0.12)',
}));

export const StatusValue = styled('span')({
  color: '#6ee7b7',
  fontWeight: 500,
  fontFamily:
    "'JetBrains Mono', 'Fira Code', 'SF Mono', 'Cascadia Code', monospace",
  fontSize: '0.9em',
  wordBreak: 'break-all',
});

export const StatusItem = styled(Typography)(() => ({
  display: 'grid',
  gridTemplateColumns: '160px 1fr',
  gap: theme.spacing(1.5),
  padding: theme.spacing(0.5, 1),
  color: '#94a3b8',
  fontWeight: 500,
  fontSize: '0.9em',
  marginBottom: theme.spacing(0.25),
  borderRadius: 4,
  transition: 'background 0.15s',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.02)',
  },
}));

export const ConnectionTitle = styled(Typography)({
  color: '#fbbf24',
  fontWeight: 600,
  fontFamily:
    "'JetBrains Mono', 'Fira Code', 'SF Mono', 'Cascadia Code', monospace",
  fontSize: '0.9em',
  marginBottom: theme.spacing(0.5),
});

export const CallId = styled(Typography)({
  color: '#6ee7b7',
  fontWeight: 600,
  fontFamily:
    "'JetBrains Mono', 'Fira Code', 'SF Mono', 'Cascadia Code', monospace",
  fontSize: '0.88em',
});

export const TerminationCode = styled(Typography)({
  color: '#fca5a5',
  fontWeight: 500,
  fontSize: '0.88em',
});

export const CallLogItem = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(0.75, 1),
  borderRadius: 6,
  color: '#e5e7eb',
  fontWeight: 500,
  marginBottom: theme.spacing(0.5),
  background: 'rgba(255, 255, 255, 0.02)',
  border: '1px solid rgba(255, 255, 255, 0.04)',
  transition: 'background 0.15s',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.04)',
  },
}));

export const StyledDivider = styled(Divider)(() => ({
  margin: theme.spacing(2, 0),
  backgroundColor: 'rgba(129, 140, 248, 0.1)',
}));

export const TabButton = styled('button')<{ $active?: boolean }>(
  ({ $active }) => ({
    background: $active
      ? 'rgba(99, 102, 241, 0.12)'
      : 'rgba(255, 255, 255, 0.02)',
    color: $active ? '#a5b4fc' : '#64748b',
    border: $active
      ? '1px solid rgba(99, 102, 241, 0.25)'
      : '1px solid rgba(255, 255, 255, 0.06)',
    borderRadius: 6,
    padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)}`,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    transition: 'all 0.15s ease',
    '&:hover': {
      color: '#a5b4fc',
      background: 'rgba(99, 102, 241, 0.08)',
      borderColor: 'rgba(99, 102, 241, 0.2)',
    },
  })
);

export const ToolbarRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2.5),
}));

export const TabBar = styled(Box)(() => ({
  display: 'flex',
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.75),
  background: 'rgba(0, 0, 0, 0.2)',
  borderRadius: 8,
  border: '1px solid rgba(255, 255, 255, 0.04)',
  flex: 1,
}));

export const FontSizeControls = styled(Box)({
  display: 'flex',
  gap: 2,
  flexShrink: 0,
});

export const FontSizeButton = styled('button')({
  background: 'rgba(255, 255, 255, 0.04)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: 4,
  color: '#94a3b8',
  cursor: 'pointer',
  fontSize: '0.65rem',
  fontWeight: 700,
  padding: '3px 6px',
  lineHeight: 1,
  transition: 'all 0.15s ease',
  '&:hover:not(:disabled)': {
    color: '#a5b4fc',
    background: 'rgba(99, 102, 241, 0.1)',
    borderColor: 'rgba(99, 102, 241, 0.25)',
  },
  '&:disabled': {
    opacity: 0.3,
    cursor: 'default',
  },
});

export const CloseButton = styled('button')({
  background: 'rgba(248, 113, 113, 0.08)',
  border: '1px solid rgba(248, 113, 113, 0.3)',
  borderRadius: 6,
  color: '#fca5a5',
  cursor: 'pointer',
  width: 24,
  height: 24,
  lineHeight: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.8rem',
  fontWeight: 700,
  flexShrink: 0,
  transition: 'all 0.15s ease',
  '&:hover': {
    color: '#fecaca',
    background: 'rgba(248, 113, 113, 0.16)',
    borderColor: 'rgba(248, 113, 113, 0.45)',
  },
});
